"use client";

import React from "react";
import { Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function NextStepForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="86221" className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="w-16 h-16 bg-primary/10 rounded-sm flex items-center justify-center mb-4">
          <CircleCheck className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-black text-white uppercase tracking-tight">{successMessage}</h3>
        <p className="text-zinc-400 max-w-md mx-auto">
          Wir haben Ihre Transportdetails erhalten und erstellen Ihnen innerhalb kürzester Zeit ein individuelles Angebot.
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-zinc-700 text-zinc-300 hover:bg-zinc-800 dark:border-zinc-700 dark:text-zinc-300 dark:hover:bg-zinc-800">
          NEUE ANFRAGE STARTEN
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="name" rules={{
        required: "Bitte geben Sie Ihren Namen an."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-zinc-400 font-bold uppercase text-[10px] tracking-widest">Name</FormLabel>
              <FormControl>
                <Input placeholder="IHR VOLLSTÄNDIGER NAME" className="bg-transparent border-0 border-b border-zinc-700 rounded-none text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-primary dark:bg-transparent dark:border-zinc-700 dark:text-white" {...field} />
              </FormControl>
              <FormDescription className="text-zinc-600 text-[10px] uppercase tracking-widest">Vor- und Nachname</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "E-Mail-Adresse ist erforderlich.",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Bitte geben Sie eine gültige E-Mail-Adresse ein."
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-zinc-400 font-bold uppercase text-[10px] tracking-widest">E-Mail-Adresse</FormLabel>
              <FormControl>
                <Input type="email" placeholder="IHRE@EMAIL.DE" className="bg-transparent border-0 border-b border-zinc-700 rounded-none text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-primary dark:bg-transparent dark:border-zinc-700 dark:text-white" {...field} />
              </FormControl>
              <FormDescription className="text-zinc-600 text-[10px] uppercase tracking-widest">Für die Angebotszustellung</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="text-zinc-400 font-bold uppercase text-[10px] tracking-widest">Telefonnummer</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+49 (0) 123 456 789" className="bg-transparent border-0 border-b border-zinc-700 rounded-none text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-primary dark:bg-transparent dark:border-zinc-700 dark:text-white" {...field} />
              </FormControl>
              <FormDescription className="text-zinc-600 text-[10px] uppercase tracking-widest">Für Rückfragen zum Transport</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="interest" rules={{
        required: "Bitte wählen Sie eine Leistung aus."
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-zinc-400 font-bold uppercase text-[10px] tracking-widest">Was benötigen Sie?</FormLabel>
              <FormControl>
                <select className="flex h-10 w-full rounded-none border-0 border-b border-zinc-700 bg-transparent px-0 py-1 text-base text-white shadow-none transition-colors outline-none focus-visible:border-primary focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm dark:bg-transparent dark:border-zinc-700 dark:text-white" {...field}>
                  <option value="" disabled className="bg-zinc-900">Bitte Leistung wählen</option>
                  {["Kurier- & Expressfahrt", "Antiquitätentransport", "Umzugsservice", "Entrümpelung"].map((option, i) => <option key={option} value={option} data-index={i} className="bg-zinc-900 text-white">
                      {option}
                    </option>)}
                </select>
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="message" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-zinc-400 font-bold uppercase text-[10px] tracking-widest">Details zum Transport</FormLabel>
            <FormControl>
              <Textarea placeholder="Was, wann und wohin möchten Sie transportieren? Beschreiben Sie kurz Ihr Vorhaben..." className="min-h-[120px] bg-transparent border-0 border-b border-zinc-700 rounded-none text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-primary dark:bg-transparent dark:border-zinc-700 dark:text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="image" render={({
      field: {
        value,
        onChange,
        ...fieldProps
      }
    }) => <FormItem>
            <FormLabel className="text-zinc-400 font-bold uppercase text-[10px] tracking-widest">Bild hochladen</FormLabel>
            <FormControl>
              <Input type="file" placeholder="Bild hochladen" className="bg-transparent border-0 border-b border-zinc-700 rounded-none text-white placeholder:text-zinc-600 focus-visible:ring-0 focus-visible:border-primary dark:bg-transparent dark:border-zinc-700 dark:text-white file:text-zinc-400 file:font-bold file:uppercase file:text-[10px] file:tracking-widest file:cursor-pointer file:mr-4" onChange={e => onChange(e.target.files?.[0])} {...fieldProps} value={undefined} />
            </FormControl>
            <FormDescription className="text-zinc-600 text-[10px] uppercase tracking-widest">Optional: Foto der Ware für eine genauere Kalkulation</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-red-950/20 border border-red-900 rounded-sm flex items-start gap-3">
          <CircleAlert className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-sm h-12 text-base font-black bg-white text-black hover:bg-zinc-200 shadow-xl shadow-white/5 transition-all uppercase tracking-tight">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Wird gesendet...
          </> : 'Unverbindliches Angebot sichern'}
      </Button>
    </Form>;
}
export default function YourNextStep() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const steps = [{
    number: 1,
    label: "ANFRAGE"
  }, {
    number: 2,
    label: "KALKULATION"
  }, {
    number: 3,
    label: "TRANSPORT"
  }];
  return;
}